import type { CertificatePdfData } from '@components/certificate-pdf/types.ts';

/**
 * Builds the certificate PDF in the browser.
 * The PDF library (~450 KB gzip) and the fonts are loaded only on the first call,
 * so they are not part of the main bundle.
 *
 * Example:
 *   const blob = await generateCertificatePdf(data);
 *   downloadBlob(blob, `${data.fullName}${data.certificateVersion}.pdf`); // from '@utils/download.ts'
 */
export const generateCertificatePdf = async (data: CertificatePdfData): Promise<Blob> => {
    const [{ pdf }, { CertificateDocument }, { registerCertificateFonts }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@components/certificate-pdf/CertificateDocument.tsx'),
        import('@components/certificate-pdf/fonts.ts'),
    ]);

    registerCertificateFonts();
    // TODO: fix Buffer is not defined in the next version
    // Known library bug, safe to ignore: the console shows "Buffer is not defined" once per image.
    // @react-pdf/layout (fetchImage) calls the Node-only `Buffer.isBuffer` after the image is already loaded,
    // so the PDF is correct. Only the image cache key is missing, so an image repeated on every page
    // (e.g. the footer badge) is embedded per page and the file is a little bigger.
    // Not fixed in our versions, which are also the newest ones (checked 2026-10-29):
    // @react-pdf/renderer 4.9.0 and @react-pdf/layout 5.2.0.
    return pdf(<CertificateDocument data={data} />).toBlob();
};
